import React from 'react';
import { GridRenderCellParams } from '@local/finsera-core/src/libs/mui/data-grid';
import { COLOR_ERROR, COLOR_SUCCESS } from '@local/finsera-core/src/theme/layout-size';
import { Box } from '@mui/material';

import { _formatFloat, _formatInteger } from './tabular-formatters';

const toDollars = (value: number, decimals = 2) => {
  const base = {
    commasOnThousands: true,
    prefix: '$',
  };

  if (decimals === 0) return _formatInteger({ value, ...base });
  return _formatFloat({ value, roundDigits: decimals, ...base });
};

const toPercentage = (value: number, decimals = 2) => {
  const base = {
    suffix: '%',
    multiplier: 100,
  };

  let formattedValue;

  if (decimals === 0) {
    formattedValue = _formatInteger({ value, ...base });
  } else {
    formattedValue = _formatFloat({ value, roundDigits: decimals, ...base });
  }

  if (formattedValue === '-0.0%') {
    formattedValue = '0.0%';
  }

  return formattedValue;
};

const toDecimals = (value: number, decimals: number) =>
  _formatFloat({
    value,
    roundDigits: decimals,
    commasOnThousands: true,
  });

const toInteger = (value: number, withNA = false) => {
  if (withNA && value === null) return 'N/A';
  return _formatInteger({
    value,
    commasOnThousands: true,
  });
};

const getPercentColorStyles = (value: number, precision = 1) => {
  const formattedValue = toPercentage(value, precision);

  const numericValue = parseFloat(String(formattedValue).replace('%', ''));
  const isEffectivelyZero = Math.abs(numericValue) < Math.pow(10, -precision);

  if (isEffectivelyZero) {
    return {
      backgroundColor: 'transparent',
      color: '#627178',
    };
  }

  const isPositive = value > 0;
  const intensity = Math.min(255, 100 + Math.floor(Math.abs(value) * 3));

  return {
    backgroundColor: isPositive ? `rgba(0, ${intensity}, 0, 0.05)` : `rgba(${intensity}, 0, 0, 0.05)`,
    color: isPositive ? COLOR_SUCCESS : COLOR_ERROR,
  };
};

const toPercentageColouredCell = (params: GridRenderCellParams, decimals = 1) => {
  const value = params.value;
  const formattedValue = toPercentage(value, decimals);
  const { color } = getPercentColorStyles(value);

  return (
    <Box
      sx={{
        color,
        textAlign: 'right',
        height: 'calc(100% - 8px)',
        borderRadius: '100px',
        margin: '2px 0',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
      }}
    >
      {formattedValue}
    </Box>
  );
};

export { getPercentColorStyles, toDecimals, toDollars, toInteger, toPercentage, toPercentageColouredCell };
